import { IMessage } from "../../services";
import { forceDownloadDocumentById, forceDownloadFile } from "../../services/fileDownloadService";
import {
  AttachedFile,
  IColumnsFieldsObject,
  IFileAttachment,
  IMessageAppealStrategy,
  IMessageClaimDetail,
  RawMessage,
  StatusMessageState,
  VALID_ACTIONS,
  VALID_PHASES,
} from "../../types/chat";

const FIRST_JSON_FENCE = /```\s*json\s*\r?\n?([\s\S]*?)```/i;
const ALL_JSON_FENCES = /```\s*json\s*\r?\n?[\s\S]*?```/gi;

/** Structural tags that carry content even with no visible text (e.g. a freshly inserted, still-empty table). */
const STRUCTURAL_TAG_REGEX = /<(table|img|hr|iframe|video|audio)[\s/>]/i;

export type MessageContentPart =
  | { type: "markdown"; content: string }
  | { type: "json"; content: IMessageClaimDetail | IMessageAppealStrategy };

export class ChatHelper {
  static getChatIdFromPathname(): string | null {
    if (window.location.pathname === "/") {
      return null;
    }
    if (window.location.pathname.includes("/channels/")) {
      const id = window.location.pathname.split("/channels/").pop() || null;
      // "new" is a virtual route for draft chats — not a real chat ID
      return id === "new" ? null : id;
    }

    return null;
  }
  static getChannelTypeFromPathname() {
    if (window.location.pathname.includes("/policy-questions")) {
      return "policy-questions";
    }
    if (window.location.pathname.includes("/contract-research")) {
      return "contract-research";
    }
    if (window.location.pathname.includes("/appeal-claim")) {
      return "appeal-claim";
    }
    if (window.location.pathname.includes("/appeal-review")) {
      return "appeal-review";
    }
    if (window.location.pathname.includes("/documents-review")) {
      return "documents-review";
    }
    if (window.location.pathname.includes("/pre-service-review")) {
      return "pre-service-review";
    }

    return null;
  }
  static extractChatFiles(messages: IMessage[], chatFiles?: IFileAttachment[]) {
    let files = [] as IFileAttachment[];

    if (!messages || messages.length === 0) {
      if (chatFiles && chatFiles.length > 0) {
        return { files: chatFiles };
      }
      return { files };
    }

    // Extract files from chat messages
    files = messages
      ?.map((message) => message.files || [])
      ?.flat()
      ?.filter(
        (message, index, list) =>
          index === list.findIndex((item) => item.id === message.id),
      );

    // Merge in unmirrored chat-level files and deduplicate
    if (chatFiles && chatFiles.length > 0) {
      files = [...files, ...chatFiles].filter(
        (file, index, list) =>
          index === list.findIndex((item) => item.id === file.id),
      );
    }

    return { files };
  }

  /** Coerces string/number page keys (e.g. from JSON) to numeric indices for the PDF viewer. */
  static normalizePageRotationMapping(
    mapping?: Record<number | string, number> | null,
  ): Record<number, number> {
    if (!mapping) return {};
    const out: Record<number, number> = {};

    for (const [key, value] of Object.entries(mapping)) {
      if (typeof value !== "number") continue;

      const pageIndex = Number(key);
      if (!Number.isNaN(pageIndex)) out[pageIndex] = value;
    }
    return out;
  }

  static readFileAsText(file: File): Promise<AttachedFile> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve({ name: file.name, content: reader.result as string });
      if (
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf")
      ) {
        reader.readAsDataURL(file); // base64 for PDFs
      } else {
        reader.readAsText(file);
      }
    });
  }

  /** Maps WebSocket-specific file field aliases to the canonical API shape.
   * WS sends `filename` + `url`; REST API sends `original_name` + `file_url`. */
  private static normalizeFileFields(file: any): any {
    if (!file || typeof file !== "object") return file;
    const out = { ...file };
    if (!out.original_name && out.filename) out.original_name = out.filename;
    if (!out.file_url && out.url) out.file_url = out.url;
    return out;
  }

  // Helper function to deduplicate files by ID, URL, or name
  static deduplicateFiles = (files: any[]): any[] => {
    if (!files || !Array.isArray(files)) {
      return [];
    }

    const seen = new Set<string>();
    const uniqueFiles: any[] = [];

    for (const rawFile of files) {
      if (!rawFile) continue;

      const file = ChatHelper.normalizeFileFields(rawFile);

      const fileId = file.id;
      const fileUrl = file.file_url || (file as any).url;
      const fileName = file.name || file.file_name || file.original_name;
      const key = fileId || fileUrl || fileName;
      const fileWithRotation = file?.page_rotation_mapping
        ? {
            ...file,
            page_rotation_mapping: this.normalizePageRotationMapping(
              file.page_rotation_mapping as Record<number | string, number>,
            ),
          }
        : file;

      if (key && !seen.has(key)) {
        seen.add(key);
        uniqueFiles.push(fileWithRotation);
      } else if (!key) {
        uniqueFiles.push(fileWithRotation);
      }
    }

    return uniqueFiles;
  };

  static normalizeMessage = (chatId: string, raw: RawMessage): IMessage => {
    // Letter HTML still rides on history rows until REV-2519; the bubble reads
    // the letter from ChatStore.chatLetters, so it never lands on IMessage.
    const { letter_content: _letterHtml, ...rest } = raw;
    const sender: "me" | "other" =
      raw.sender || (raw.role === "user" ? "me" : "other");
    const text = raw.content || raw.text || "";
    const timestamp = raw.created_at
      ? new Date(raw.created_at).getTime()
      : Date.now();

    return {
      ...rest,
      id: raw.id,
      channel: chatId,
      data: text,
      sender,
      timestamp,
      ref_id: raw.ref_id || raw.metadata?.ref_id,
      files: this.deduplicateFiles(raw.files || []),
      agent_name: raw.agent_name,
      metadata: raw.metadata,
      // Per-message author (REV-2177): original sender identity for cloned/shared chats
      author_name: raw.author_name,
      author_avatar: raw.author_avatar,
      // Extract contextWarning from direct field or from metadata (for API responses)
      // Handle both contextWarning and contentWarning (typo fix) for backward compatibility
      contextWarning:
        raw.contextWarning ||
        (raw.metadata && typeof raw.metadata === "object"
          ? raw.metadata.contextWarning || raw.metadata.contentWarning || false
          : false),
    };
  };

  // Extract file ID from URL
  static extractFileIdFromUrl = (url: string): string | null => {
    if (!url) return null;
    // Extract ID from URL pattern: https://promptwrx-files.s3.amazonaws.com/files/{id}-{filename}
    const match = url.match(/\/files\/([a-f0-9-]+)-/);
    return match ? match[1] : null;
  };

  static downloadFile = async (file: IFileAttachment) => {
    try {
      let fileId = file.id as string | null;
      if (!fileId) {
        const fileUrl = file.file_url || file.url;
        fileId = ChatHelper.extractFileIdFromUrl(fileUrl as string);
      }

      const filename =
        file.display_name || file.original_name || file.file_name || file.name || undefined;

      if (fileId) {
        if (file.is_document) {
          await forceDownloadDocumentById(fileId, filename);
        } else {
          await forceDownloadFile(fileId, filename);
        }
      } else {
        const fileUrl = file.file_url || file.url;
        if (fileUrl) {
          const link = document.createElement("a");
          link.href = fileUrl;
          link.download = filename || "";
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  /** Extract StatusMessageState from a message when metadata.status is truthy (e.g. from API fetch) */
  static statusFromMessage(
    message: RawMessage | IMessage,
  ): StatusMessageState | null {
    const meta = (message as any).metadata;

    if (!meta || (meta.status !== true && meta.status !== 1 && !meta.status)) {
      return null;
    }

    const action = VALID_ACTIONS.includes(meta.action)
      ? meta.action
      : "processing";
    const phase = VALID_PHASES.includes(meta.phase) ? meta.phase : "complete";
    const timestamp =
      typeof (message as any).timestamp === "number"
        ? (message as any).timestamp
        : (message as any).created_at
          ? new Date((message as any).created_at).getTime()
          : Date.now();

    return {
      tool: typeof meta.tool === "string" ? meta.tool : "tool",
      action,
      message:
        typeof meta.message === "string"
          ? meta.message
          : (message as any).data ||
            (message as any).content ||
            (message as any).text ||
            "",
      phase,
      duration: typeof meta.duration === "number" ? meta.duration : 0,
      chat_duration:
        typeof meta.chat_duration === "number" ? meta.chat_duration : 0,
      timestamp,
      files:
        message?.files && message.files.length > 0
          ? message.files.map((file) =>
              file.page_rotation_mapping
                ? {
                    ...file,
                    page_rotation_mapping: ChatHelper.normalizePageRotationMapping(
                      file.page_rotation_mapping as Record<
                        number | string,
                        number
                      >,
                    ),
                  }
                : file,
            )
          : [],
    };
  }

  /** Turn-ending assistant row (final/error/abort), not a mid-turn partial or
   * agent_result blob. Matches response_worker terminal metadata flags. */
  static isTerminalAssistantRow(row: RawMessage | IMessage): boolean {
    const r = row as any;
    if (r.role !== "assistant" && r.sender !== "other") return false;
    if (r.type === "stream" || r.type === "partial" || r.type === "agent_result") {
      return false;
    }
    const meta = r.metadata || {};
    if (meta.partial || meta.agent_result) return false;
    return true;
  }

  /** Latest turn-ending assistant row time (ms), or 0 if none. `partial` and
   * `agent_result` rows don't end a turn (response_worker.py metadata flags). */
  static lastTerminalAssistantAt(rows: Array<RawMessage | IMessage>): number {
    let max = 0;
    for (const r of rows as any[]) {
      if (!ChatHelper.isTerminalAssistantRow(r)) continue;
      const t =
        typeof r.timestamp === "number"
          ? r.timestamp
          : r.created_at
            ? new Date(r.created_at).getTime()
            : 0;
      if (t > max) max = t;
    }
    return max;
  }

  /** Order-insensitive: a tool is in flight iff its newest start/progress row is
   * newer than both its own newest terminal (complete/failed) row and the last
   * turn-ending assistant row. Fixes the multi-worker persist race where the
   * `complete` row can land in the DB before the `start` row (REV-2419) — a
   * "last row per tool" read would otherwise treat that tool as still running. */
  static inFlightStatuses(
    statuses: StatusMessageState[],
    lastTerminalAt: number,
  ): StatusMessageState[] {
    const ts = (s: StatusMessageState) => (Number.isFinite(s.timestamp) ? s.timestamp : 0);
    const open = new Map<string, StatusMessageState>();
    const closedAt = new Map<string, number>();
    for (const s of statuses) {
      if (s.phase === "start" || s.phase === "progress") {
        const cur = open.get(s.tool);
        if (!cur || ts(s) >= ts(cur)) open.set(s.tool, s);
      } else {
        closedAt.set(s.tool, Math.max(closedAt.get(s.tool) ?? 0, ts(s)));
      }
    }
    // `0` (no closing row / no terminal assistant row yet) means "nothing to
    // compare against" here, not a real timestamp to beat.
    return [...open.values()].filter((s) => {
      const closed = closedAt.get(s.tool) ?? 0;
      return (closed === 0 || ts(s) > closed) && (lastTerminalAt === 0 || ts(s) > lastTerminalAt);
    });
  }

  /** Raw JSON inside the first Markdown ```json … ``` fence, or null. */
  static extractJsonFromMessageData(
    data: string | undefined | null,
  ): IMessageClaimDetail | IMessageAppealStrategy | null {
    try {
      if (!data) {
        return null;
      }

      const match = data.match(FIRST_JSON_FENCE);
      const inner = match?.[1]?.trim();

      return inner && inner.length > 0
        ? (JSON.parse(inner) as IMessageClaimDetail | IMessageAppealStrategy)
        : null;
    } catch {
      return null;
    }
  }

  /** Removes every ```json … ``` fence from message body (e.g. before rendering). */
  static stripJsonFenceFromMessageData(
    data: string | undefined | null,
  ): string {
    try {
      if (!data) {
        return "";
      }

      return data.replace(ALL_JSON_FENCES, "").replace(/\r\n/g, "\n").trim();
    } catch {
      return data || "";
    }
  }

  /** Splits message body into ordered markdown/json segments, preserving the
   * position of any ```json … ``` fence(s) relative to surrounding markdown
   * text (e.g. intro text before the fence, follow-up notes after it). */
  static parseMessageContentParts(
    data: string | undefined | null,
  ): MessageContentPart[] {
    if (!data) {
      return [];
    }

    try {
      const parts: MessageContentPart[] = [];
      const fenceRegex = new RegExp(ALL_JSON_FENCES.source, "gi");
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = fenceRegex.exec(data)) !== null) {
        const before = data
          .slice(lastIndex, match.index)
          .replace(/\r\n/g, "\n")
          .trim();
        if (before) {
          parts.push({ type: "markdown", content: before });
        }

        const inner = match[0].match(FIRST_JSON_FENCE)?.[1]?.trim();
        if (inner) {
          try {
            parts.push({
              type: "json",
              content: JSON.parse(inner) as
                | IMessageClaimDetail
                | IMessageAppealStrategy,
            });
          } catch {
            parts.push({ type: "markdown", content: match[0].trim() });
          }
        }

        lastIndex = match.index + match[0].length;
      }

      const after = data.slice(lastIndex).replace(/\r\n/g, "\n").trim();
      if (after) {
        parts.push({ type: "markdown", content: after });
      }

      return parts;
    } catch {
      return [{ type: "markdown", content: data }];
    }
  }

  /**
   * Guards against saving over a letter mid cut/paste (e.g. Ctrl+X to replace the content) or
   * mid debounce: treat markup with no visible text and no structural elements as empty.
   */
  static isLetterContentEmpty(content: string): boolean {
    if (STRUCTURAL_TAG_REGEX.test(content)) return false;
    return !content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  }

  /** Detects the { columns, fields } object-section shape used by ChatMessageAppealStrategy
   * and ChatMessageClaimDetail, as an alternative to a plain field/value object. */
  static isColumnsFieldsObject = (
    value: Record<string, unknown>,
  ): value is Record<string, unknown> & IColumnsFieldsObject =>
    Array.isArray(value.columns) &&
    value.columns.length >= 2 &&
    value.columns.every((column) => typeof column === "string") &&
    Array.isArray(value.fields) &&
    value.fields.every(
      (field) =>
        typeof field === "object" &&
        field !== null &&
        !Array.isArray(field) &&
        "label" in field &&
        "value" in field,
    );
}
